import React, { useEffect } from 'react';
import { Button, Table } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { Card, Column, ColumnGroup } from '@cards/Card';
import DashboardUserTypeIcon from '@components/DashboardUserTypeIcon';
import DetailForm from '@components/DetailForm';
import DetailRow from '@components/DetailRow';
import IconButton from '@components/IconButton';
import { ProfilePictureAvatar } from '@components/ProfilePicture';
import PublishedStatusIcon from '@components/PublishedStatusIcon';
import { HOSPITAL_ADMIN, HOSPITAL_USER } from '@constants';
import { usePageActions } from '@hooks';
import {
  loadDirectChildOrganisationUserDetails,
  resendDirectChildOrganisationUserInvitation,
} from '@redux/directChildOrganisationAdministration/actions';
import { selectDirectChildOrganisationUserDetails } from '@redux/directChildOrganisationAdministration/selectors';
import { selectLanguageLegend } from '@utils/supportedLanguages';
import { setCurrentScreen, screens } from '../../../analytics';
import '../directChildOrganisationAdministration.less';

const DirectChildOrganisationUserDetails = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { organisationSlug } = match.params;
  const userId = Number(match.params.userId);
  const details = useSelector(state =>
    selectDirectChildOrganisationUserDetails(state, organisationSlug, userId),
  );
  const user = details.user;
  const loading = details.status === 'idle' || details.status === 'loading';
  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const editPath = `/administration/child-organisation-users/${organisationSlug}/${userId}/edit`;
  const { hasPageActionsProvider, setActions, setTitle } = usePageActions({
    title: fullName,
    actions: [],
    showBack: true,
    onBack: () => history.push('/administration/child-organisation-users'),
  });

  useEffect(() => {
    setTitle(fullName);
    setActions(
      user
        ? [
            <IconButton
              aria-label={t('directChildOrganisationAdministration:users.details.edit')}
              icon={faEdit}
              key="edit-direct-child-organisation-user"
              tooltip={t('directChildOrganisationAdministration:users.details.edit')}
              onClick={() => history.push(editPath)}
            />,
          ]
        : [],
    );
    if (fullName) document.title = fullName;
  }, [editPath, fullName, history, setActions, setTitle, t, user]);

  useEffect(() => {
    dispatch(loadDirectChildOrganisationUserDetails(organisationSlug, userId));
  }, [dispatch, organisationSlug, userId]);

  useEffect(() => {
    setCurrentScreen(screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_USER_DETAILS);
  }, []);

  if (details.status === 'failure') {
    const errorKeyByStatus = {
      403: 'forbidden',
      404: 'notFound',
      502: 'upstream',
    };
    const errorKey = errorKeyByStatus[details.errorStatus] || 'failure';

    return (
      <section role="alert">
        <p>{t(`directChildOrganisationAdministration:users.details.${errorKey}`)}</p>
        <Button
          onClick={() => dispatch(loadDirectChildOrganisationUserDetails(organisationSlug, userId))}
        >
          {t('directChildOrganisationAdministration:users.details.retry')}
        </Button>
      </section>
    );
  }

  const invitationsColumns = [
    {
      title: t('directChildOrganisationAdministration:users.details.invitationStatus'),
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: t('directChildOrganisationAdministration:users.details.invitationExpiry'),
      dataIndex: 'expires',
      key: 'expires',
      render: expires => moment(expires).format('D MMMM YYYY h:mm A'),
    },
  ];

  return (
    <>
      {!hasPageActionsProvider ? (
        <>
          <Link to="/administration/child-organisation-users">
            {t('directChildOrganisationAdministration:users.details.back')}
          </Link>
          {!loading ? (
            <>
              <h1>{fullName}</h1>
              <Link to={editPath}>
                <Button type="primary">
                  {t('directChildOrganisationAdministration:users.details.edit')}
                </Button>
              </Link>
            </>
          ) : null}
        </>
      ) : null}
      <ColumnGroup>
        <Column>
          <Card
            className="direct-child-organisation-user-detail-card"
            loading={loading}
            title={t('directChildOrganisationAdministration:users.details.userType')}
          >
            {!loading && user ? (
              <DetailForm>
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.role')}
                  value={
                    <>
                      <DashboardUserTypeIcon
                        label={false}
                        userType={user.role === 'admin' ? HOSPITAL_ADMIN : HOSPITAL_USER}
                      />{' '}
                      {t(`directChildOrganisationAdministration:users.roles.${user.role}`)}
                    </>
                  }
                />
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.organisation')}
                  value={user.organisationName}
                />
              </DetailForm>
            ) : null}
          </Card>
          <Card
            className="direct-child-organisation-user-detail-card"
            loading={loading}
            title={t('directChildOrganisationAdministration:users.details.userDetails')}
          >
            {!loading && user ? (
              <DetailForm>
                <div className="direct-child-organisation-user-details-avatar">
                  <ProfilePictureAvatar
                    url={user.profilePictureUrl}
                    displayName={fullName}
                    size={64}
                  />
                </div>
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.name')}
                  value={fullName}
                />
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.email')}
                  value={
                    <a href={`mailto:${user.email}`} rel="noopener noreferrer" target="_blank">
                      {user.email}
                    </a>
                  }
                />
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.phone')}
                  value={
                    user.phoneNumber ||
                    t('directChildOrganisationAdministration:users.details.notProvided')
                  }
                />
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.language')}
                  value={selectLanguageLegend(user.language)}
                />
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.careTeamMember')}
                  value={t(`common:${user.careTeamMember ? 'yes' : 'no'}`)}
                />
              </DetailForm>
            ) : null}
          </Card>
        </Column>
        <Column>
          <Card
            className="direct-child-organisation-user-access-card"
            loading={loading}
            title={t('directChildOrganisationAdministration:users.details.access')}
          >
            {!loading && user ? (
              <DetailForm>
                <DetailRow
                  label={t('directChildOrganisationAdministration:users.details.activation')}
                  value={
                    <span className="activation-status">
                      <PublishedStatusIcon published={user.activated} />
                      {t(
                        `directChildOrganisationAdministration:users.activation.${
                          user.activated ? 'activated' : 'notActivated'
                        }`,
                      )}
                    </span>
                  }
                />
              </DetailForm>
            ) : null}
            {!loading && user && !user.activated ? (
              <Button
                disabled={details.invitations.resendStatus === 'loading'}
                type="primary"
                onClick={() =>
                  dispatch(resendDirectChildOrganisationUserInvitation(organisationSlug, userId))
                }
              >
                {t('directChildOrganisationAdministration:users.details.resend.action')}
              </Button>
            ) : null}
          </Card>
          <Card
            className="direct-child-organisation-user-detail-card"
            loading={loading}
            title={t('directChildOrganisationAdministration:users.details.invitationHistory')}
            noPadding
          >
            {!loading ? (
              <Table
                columns={invitationsColumns}
                dataSource={details.invitations.items}
                locale={{
                  emptyText: t('directChildOrganisationAdministration:users.details.noInvitations'),
                }}
                pagination={false}
                rowKey="id"
              />
            ) : null}
          </Card>
        </Column>
      </ColumnGroup>
    </>
  );
};

export default DirectChildOrganisationUserDetails;
